import React, { useState, useEffect } from 'react';
import { locationService } from '../../api/locationService';
import { ChevronDown } from 'lucide-react';

/**
 * Location Selector Component
 * Provides cascading dropdowns for Country -> County -> Sub County -> Ward selection
 * 
 * @param {Object} props
 * @param {Function} props.onLocationChange - Callback when location is selected: (location) => void
 * @param {Object} props.initialValues - Initial location values: { countryId, countyId, subCountyId, wardId }
 * @param {boolean} props.showAll - Show all locations including inactive (default: false)
 * @param {boolean} props.horizontal - Display fields in a horizontal row (default: false)
 * @param {boolean} props.hideCountry - Hide Country dropdown; keep Kenya (125) as the silent default
 */
const LocationSelector = ({
  onLocationChange,
  initialValues = {},
  showAll = false,
  horizontal = false,
  hideCountry = false,
}) => {
  const [countries, setCountries] = useState([]);
  const [counties, setCounties] = useState([]);
  const [subCounties, setSubCounties] = useState([]);
  const [wards, setWards] = useState([]);
  
  const [selectedCountry, setSelectedCountry] = useState(initialValues.countryId || '125');
  const [selectedCounty, setSelectedCounty] = useState(initialValues.countyId || '');
  const [selectedSubCounty, setSelectedSubCounty] = useState(initialValues.subCountyId || '');
  const [selectedWard, setSelectedWard] = useState(initialValues.wardId || '');
  
  const [loading, setLoading] = useState({
    countries: false,
    counties: false,
    subCounties: false,
    wards: false
  });

  // Load countries only when the Country dropdown is visible.
  useEffect(() => {
    if (!hideCountry) {
      loadCountries();
    }
  }, [hideCountry]);

  // Load counties when country changes
  useEffect(() => {
    if (selectedCountry) {
      loadCounties(selectedCountry);
    } else {
      setCounties([]);
      setSelectedCounty('');
    }
  }, [selectedCountry]);

  // Load sub counties when county changes
  useEffect(() => {
    if (selectedCounty) {
      loadSubCounties(selectedCounty);
    } else {
      setSubCounties([]);
      setSelectedSubCounty('');
    }
  }, [selectedCounty]);

  // Load wards when sub county changes
  useEffect(() => {
    if (selectedSubCounty) {
      loadWards(selectedSubCounty);
    } else {
      setWards([]);
      setSelectedWard('');
    }
  }, [selectedSubCounty]);

  // Notify parent when location changes
  useEffect(() => {
    if (onLocationChange) {
      onLocationChange({
        countryId: selectedCountry ? parseInt(selectedCountry) : null,
        countyId: selectedCounty ? parseInt(selectedCounty) : null,
        subCountyId: selectedSubCounty ? parseInt(selectedSubCounty) : null,
        wardId: selectedWard ? parseInt(selectedWard) : null,
        countryName: countries.find(c => c.id === parseInt(selectedCountry))?.name || null,
        countyName: counties.find(c => c.id === parseInt(selectedCounty))?.name || null,
        subCountyName: subCounties.find(s => s.id === parseInt(selectedSubCounty))?.name || null,
        wardName: wards.find(w => w.id === parseInt(selectedWard))?.name || null
      });
    }
  }, [selectedCountry, selectedCounty, selectedSubCounty, selectedWard]);

  const loadCountries = async () => {
    setLoading(prev => ({ ...prev, countries: true }));
    try {
      const response = showAll 
        ? await locationService.getAllCountries()
        : await locationService.getActiveCountries();
      
      // Handle different response structures
      let countriesData = [];
      if (response?.data?.data?.data) {
        countriesData = response.data.data.data;
      } else if (response?.data?.data) {
        countriesData = response.data.data;
      } else if (response?.data) {
        countriesData = response.data;
      } else if (Array.isArray(response)) {
        countriesData = response;
      }
      
      setCountries(Array.isArray(countriesData) ? countriesData : []);
      //console.log('Loaded countries:', countriesData);
    } catch (error) {
      console.error('Error loading countries:', error);
      console.error('Error details:', error.response || error.message);
      setCountries([]);
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  };

  const loadCounties = async (countryId) => {
    if (!countryId) {
      setCounties([]);
      return;
    }
    
    setLoading(prev => ({ ...prev, counties: true }));
    try {
      const response = showAll
        ? await locationService.getAllCounties(countryId)
        : await locationService.getActiveCounties(countryId);
      
      // Handle different response structures
      let countiesData = [];
      if (response?.data?.data?.data) {
        countiesData = response.data.data.data;
      } else if (response?.data?.data) {
        countiesData = response.data.data;
      } else if (response?.data) {
        countiesData = response.data;
      } else if (Array.isArray(response)) {
        countiesData = response;
      }
      
      setCounties(Array.isArray(countiesData) ? countiesData : []);
      //console.log('Loaded counties for countryId', countryId, ':', countiesData);
    } catch (error) {
      console.error('Error loading counties:', error);
      console.error('Error details:', error.response || error.message);
      setCounties([]);
    } finally {
      setLoading(prev => ({ ...prev, counties: false }));
    }
  };

  const loadSubCounties = async (countyId) => {
    if (!countyId) {
      setSubCounties([]);
      return;
    }
    
    setLoading(prev => ({ ...prev, subCounties: true }));
    try {
      const response = showAll
        ? await locationService.getAllSubCounties(countyId)
        : await locationService.getActiveSubCounties(countyId);
      
      // Handle different response structures
      let subCountiesData = [];
      if (response?.data?.data?.data) {
        subCountiesData = response.data.data.data;
      } else if (response?.data?.data) {
        subCountiesData = response.data.data;
      } else if (response?.data) {
        subCountiesData = response.data;
      } else if (Array.isArray(response)) {
        subCountiesData = response;
      }
      
      setSubCounties(Array.isArray(subCountiesData) ? subCountiesData : []);
    } catch (error) {
      console.error('Error loading sub counties:', error);
      setSubCounties([]);
    } finally {
      setLoading(prev => ({ ...prev, subCounties: false }));
    }
  };

  const loadWards = async (subCountyId) => {
    if (!subCountyId) {
      setWards([]);
      return;
    }
    
    setLoading(prev => ({ ...prev, wards: true }));
    try {
      const response = showAll
        ? await locationService.getAllWards(subCountyId)
        : await locationService.getActiveWards(subCountyId);
      
      // Handle different response structures
      let wardsData = [];
      if (response?.data?.data?.data) {
        wardsData = response.data.data.data;
      } else if (response?.data?.data) {
        wardsData = response.data.data;
      } else if (response?.data) {
        wardsData = response.data;
      } else if (Array.isArray(response)) {
        wardsData = response;
      }
      
      setWards(Array.isArray(wardsData) ? wardsData : []);
    } catch (error) {
      console.error('Error loading wards:', error);
      setWards([]);
    } finally {
      setLoading(prev => ({ ...prev, wards: false }));
    }
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;
    setSelectedCountry(value);
    setSelectedCounty('');
    setSelectedSubCounty('');
    setSelectedWard('');
  };

  const handleCountyChange = (e) => {
    const value = e.target.value;
    setSelectedCounty(value);
    setSelectedSubCounty('');
    setSelectedWard('');
  };

  const handleSubCountyChange = (e) => {
    const value = e.target.value;
    setSelectedSubCounty(value);
    setSelectedWard('');
  };

  const handleWardChange = (e) => {
    setSelectedWard(e.target.value);
  };

  const horizontalCols = hideCountry
    ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
    : 'grid grid-cols-1 md:grid-cols-4 gap-4';

  return (
    <div className={horizontal ? horizontalCols : "space-y-4"}>
      {/* Country — Kenya (125) remains the default even when hidden */}
      {!hideCountry && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Country <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
          </label>
          <div className="relative">
            <select
              value={selectedCountry}
              onChange={handleCountryChange}
              disabled={loading.countries}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10"
            >
              <option value="">Select Country</option>
              {countries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          {loading.countries && (
            <p className="text-xs text-gray-500 mt-1">Loading countries...</p>
          )}
        </div>
      )}

      {/* County */}
      {selectedCountry && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            County
          </label>
          <div className="relative">
            <select
              value={selectedCounty}
              onChange={handleCountyChange}
              disabled={loading.counties || !selectedCountry}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10"
            >
              <option value="">Select County</option>
              {counties.length === 0 && !loading.counties && (
                <option value="" disabled>No counties available</option>
              )}
              {counties.map((county) => (
                <option key={county.id} value={county.id}>
                  {county.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          {loading.counties && (
            <p className="text-xs text-gray-500 mt-1">Loading counties...</p>
          )}
          {!loading.counties && counties.length === 0 && selectedCountry && (
            <p className="text-xs text-gray-500 mt-1">No counties found for selected country</p>
          )}
        </div>
      )}

      {/* Sub County */}
      {selectedCounty && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Sub County
          </label>
          <div className="relative">
            <select
              value={selectedSubCounty}
              onChange={handleSubCountyChange}
              disabled={loading.subCounties || !selectedCounty}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10"
            >
              <option value="">Select Sub County</option>
              {subCounties.map((subCounty) => (
                <option key={subCounty.id} value={subCounty.id}>
                  {subCounty.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          {loading.subCounties && (
            <p className="text-xs text-gray-500 mt-1">Loading sub counties...</p>
          )}
        </div>
      )}

      {/* Ward */}
      {/* {selectedSubCounty && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ward
          </label>
          <div className="relative">
            <select
              value={selectedWard}
              onChange={handleWardChange}
              disabled={loading.wards || !selectedSubCounty}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none pr-10"
            >
              <option value="">Select Ward</option>
              {wards.map((ward) => (
                <option key={ward.id} value={ward.id}>
                  {ward.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          </div>
          {loading.wards && (
            <p className="text-xs text-gray-500 mt-1">Loading wards...</p>
          )}
        </div>
      )} */}
    </div>
  );
};

export default LocationSelector;

